import { useState } from 'react';
import {
  Users,
  Bell,
  CheckCircle,
  Heart,
  Sparkles,
  Cake,
  CalendarCheck,
  UserCheck,
  ArrowRight,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { requestNotificationPermission } from '../lib/firebase';

interface OnboardingProps {
  onComplete: () => void;
}

export default function Onboarding({ onComplete }: OnboardingProps) {
  const [step, setStep] = useState(0);

  const totalSteps = 3;

  const next = () => {
    if (step < totalSteps - 1) setStep(step + 1);
    else onComplete();
  };

  const handleEnableNotifications = async () => {
    await requestNotificationPermission().catch(() => {});
    next();
  };

  return (
    <div className="fixed inset-0 z-[90] bg-[#f8f5f5] flex flex-col max-w-md mx-auto">
      {/* Progress bar */}
      <div className="flex gap-2 px-6 pt-6">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'flex-1 h-1.5 rounded-full transition-colors duration-300',
              i <= step ? 'bg-slate-800' : 'bg-slate-200',
            )}
          />
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {step === 0 && <WelcomeStep />}
        {step === 1 && <NotificationsStep />}
        {step === 2 && <ReadyStep />}
      </div>

      <div className="p-6 pb-6 flex flex-col gap-3">
        <button
          onClick={step === 1 ? handleEnableNotifications : next}
          className="w-full bg-slate-800 text-white font-bold py-3 rounded-full flex items-center justify-center gap-2 active:scale-[0.98] transition-transform shadow-lg"
        >
          {step === 0 && 'Continue'}
          {step === 1 && 'Enable Notifications'}
          {step === 2 && (
            <>
              Let's Go <ArrowRight size={18} />
            </>
          )}
        </button>
        {step === 1 && (
          <button
            onClick={next}
            className="text-slate-500 text-sm font-medium py-2 hover:text-slate-700 transition-colors"
          >
            Maybe later
          </button>
        )}
      </div>
    </div>
  );
}

/* ============ Step 1: Welcome ============ */
function WelcomeStep() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8">
      {/* Illustration */}
      <div className="relative mb-6">
        <div className="size-36 rounded-full bg-slate-200/60 flex items-center justify-center">
          <Users size={64} className="text-slate-600" strokeWidth={1.5} />
        </div>
        {/* Floating heart */}
        <div className="absolute -top-2 -right-2 size-10 rounded-full bg-[#fa8585]/15 flex items-center justify-center">
          <Heart size={18} className="text-[#fa8585] fill-[#fa8585]" />
        </div>
        {/* Floating sparkle */}
        <div className="absolute -bottom-1 -left-3 size-9 rounded-full bg-emerald-100 flex items-center justify-center">
          <Sparkles size={16} className="text-emerald-500" />
        </div>
      </div>

      <h1 className="text-3xl font-bold text-slate-800 text-center mb-4">Welcome to Nudge!</h1>
      <p className="text-slate-600 text-center text-base leading-relaxed mb-2">
        Stay connected with the people who matter most to you.
      </p>
      <p className="text-slate-400 text-center text-sm leading-relaxed">
        We'll help you nurture your relationships with gentle reminders and meaningful insights.
      </p>
    </div>
  );
}

/* ============ Step 3: Notifications ============ */
function NotificationsStep() {
  const features = [
    {
      icon: Cake,
      label: 'Birthday reminders',
      desc: 'So you never miss one',
      bg: 'bg-[#fa8585]/10',
      color: 'text-[#fa8585]',
    },
    {
      icon: CalendarCheck,
      label: 'Weekly check-in nudges',
      desc: 'For your close friends',
      bg: 'bg-blue-50',
      color: 'text-blue-500',
    },
    {
      icon: UserCheck,
      label: 'Special occasion alerts',
      desc: 'Anniversaries, milestones & more',
      bg: 'bg-purple-50',
      color: 'text-purple-500',
    },
  ];

  return (
    <div className="flex-1 flex flex-col px-6 pt-6">
      {/* Icon */}
      <div className="flex justify-center mb-4">
        <div className="relative">
          <div className="size-20 rounded-full bg-[#fa8585]/10 flex items-center justify-center">
            <Bell size={36} className="text-slate-700" strokeWidth={1.5} />
          </div>
          <div className="absolute -top-1 -right-1 size-7 rounded-full bg-emerald-500 flex items-center justify-center border-2 border-[#f8f5f5]">
            <CheckCircle size={14} className="text-white" fill="currentColor" />
          </div>
        </div>
      </div>

      <h1 className="text-2xl font-bold text-slate-800 text-center mb-2">Stay in the loop</h1>
      <p className="text-slate-400 text-center text-sm mb-6 leading-relaxed">
        Get gentle reminders to keep your relationships strong. We'll never spam you.
      </p>

      {/* Feature cards */}
      <div className="space-y-3">
        {features.map((feature) => (
          <div
            key={feature.label}
            className="flex items-center gap-4 bg-white rounded-2xl p-4 shadow-sm"
          >
            <div
              className={cn(
                'size-12 rounded-xl flex items-center justify-center flex-shrink-0',
                feature.bg,
              )}
            >
              <feature.icon size={22} className={feature.color} />
            </div>
            <div>
              <p className="font-bold text-slate-800 text-sm">{feature.label}</p>
              <p className="text-xs text-slate-400">{feature.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ Step 4: Ready ============ */
function ReadyStep() {
  const checks = ['Account created', 'Contacts ready to add', 'Reminders configured'];

  return (
    <div className="flex-1 flex flex-col items-center px-6 pt-10">
      {/* Illustration */}
      <div className="relative mb-10">
        <div className="size-32 rounded-full bg-emerald-100/60 flex items-center justify-center">
          <CheckCircle size={56} className="text-slate-700" strokeWidth={1.5} />
        </div>
        {/* Floating dots */}
        <div className="absolute top-0 -left-4 size-8 rounded-full bg-[#fa8585]/15 flex items-center justify-center">
          <Sparkles size={14} className="text-[#fa8585]" />
        </div>
        <div className="absolute top-2 -right-3 size-7 rounded-full bg-purple-100 flex items-center justify-center">
          <Heart size={12} className="text-purple-400" />
        </div>
        <div className="absolute -bottom-2 left-4 size-4 rounded-full bg-amber-300" />
        <div className="absolute -bottom-1 right-6 size-3 rounded-full bg-emerald-500" />
      </div>

      <h1 className="text-3xl font-bold text-slate-800 text-center mb-3">Ready to Connect!</h1>
      <p className="text-slate-400 text-center text-sm mb-10">
        You're all set to strengthen your friendships.
      </p>

      {/* Checklist */}
      <div className="w-full space-y-3">
        {checks.map((label) => (
          <div key={label} className="flex items-center gap-3 bg-white rounded-2xl p-4 shadow-sm">
            <CheckCircle size={22} className="text-emerald-500 flex-shrink-0" />
            <span className="font-medium text-slate-700">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
